import { getHttpClient, HTTP_CONTENT_TYPES } from '@/utilities/httpClients'
import { internalToValue } from '@/helpers'
import { API_GATEWAY_CLIENT } from '@/Globals'

export async function logInWithCredentials(loginPayload) {
  try {
    const { data: accountData } = await getHttpClient(API_GATEWAY_CLIENT).post('/v3/login', loginPayload, {
      headers: { 'Content-Type': HTTP_CONTENT_TYPES.JSON },
    })

    return accountData
  } catch (error) {
    console.error('Failed to log in with password', error)
    throw error
  }
}

export async function logInWithGoogle(loginPayload) {
  try {
    const { data: accountData } = await getHttpClient(API_GATEWAY_CLIENT).post(
      '/v2/social/login/google',
      loginPayload,
      { headers: { 'Content-Type': HTTP_CONTENT_TYPES.FORM } },
    )

    return accountData
  } catch (error) {
    console.error('Failed to log in with Google', error)
    throw error
  }
}

export async function logInWithCognito(loginPayload) {
  try {
    const { data: accountData } = await getHttpClient(API_GATEWAY_CLIENT).post('/v2/idp/login', loginPayload, {
      headers: { 'Content-Type': HTTP_CONTENT_TYPES.FORM },
    })

    return accountData
  } catch (error) {
    console.error('Failed to log in with Cognito', error)
    throw error
  }
}

export async function reAuthenticateWithJwt(accessToken) {
  try {
    const { data: accountData } = await getHttpClient(API_GATEWAY_CLIENT).get('/v2/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return accountData
  } catch (error) {
    console.error('Failed to re-authenticate with JWT', error)
    throw error
  }
}

export async function loadLocations() {
  try {
    const { data: locations } = await getHttpClient(API_GATEWAY_CLIENT).get('/v3/locations')

    return locations
  } catch (error) {
    console.error('Failed to load locations', error)
    throw error
  }
}

export async function loadMveLocations(mveImageId) {
  try {
    const requestConfig = { params: { mveImageId } }

    const { data: mveLocations } = await getHttpClient(API_GATEWAY_CLIENT).get('/v3/locations', requestConfig)

    return mveLocations
  } catch (error) {
    console.error('Failed to load locations', error)
    throw error
  }
}

export async function loadSuppliers() {
  try {
    const { data: suppliers } = await getHttpClient(API_GATEWAY_CLIENT).get('/v2/supplier')

    return suppliers
  } catch (error) {
    console.error('Failed to load suppliers', error)
    throw error
  }
}

export async function loadPartnerPorts() {
  try {
    const { data: partnerPorts } = await getHttpClient(API_GATEWAY_CLIENT).get('/v2/dropdowns/partner/megaports')

    return partnerPorts
  } catch (error) {
    console.error('Failed to load partner ports', error)
    throw error
  }
}

export async function loadTransitEnabledMarkets() {
  try {
    const requestConfig = {
      params: { distanceBand: 'METRO', aConnectTypes: ['DEFAULT', 'VROUTER'] },
      paramsSerializer: { indexes: null },
    }

    const {
      data: { markets },
    } = await getHttpClient(API_GATEWAY_CLIENT).get('/v3/pricebook/transit/markets', requestConfig)

    return markets
  } catch (error) {
    console.error('Failed to load transit enabled markets', error)
    throw error
  }
}

/**
 * Searches for companies based on a query string to create quotes for.
 * @param {string} search The search query string. Can be either the company name or company ID.
 * @param {boolean} activeOnly Whether to only return active companies. Defaults to true.
 * @param {number} pageNumber The paginated page number to get the results for. Defaults to 1.
 * @param {number} pageSize The number of results per page. Defaults to 50.
 * @returns {Promise<Object>} A promise that resolves to the company data.
 */
export async function searchCompanies(search, activeOnly = true, pageNumber = 1, pageSize = 50) {
  // API: Encoding the search string does not work when a space is present
  // const encodedSearch = encodeURIComponent(search)
  const normalisedSearch = internalToValue(search)
  const normalisedActiveOnly = internalToValue(activeOnly)

  try {
    const requestConfig = {
      params: { query: normalisedSearch, activeOnly: normalisedActiveOnly, pageNumber, pageSize },
    }

    const { data: company } = await getHttpClient(API_GATEWAY_CLIENT).get('/secure/v2/company/search', requestConfig)

    return company
  } catch (error) {
    console.error(`Failed to load companies`, error)
    throw error
  }
}

export async function loadManagedCompanies() {
  try {
    const { data: managedCompanies } = await getHttpClient(API_GATEWAY_CLIENT).get('/v2/managedCompanies')

    return managedCompanies
  } catch (error) {
    console.error('Failed to load managed companies', error)
    throw error
  }
}

export async function loadIxTypes(locationId) {
  try {
    const requestConfig = { params: { locationId } }

    const { data: ixTypes } = await getHttpClient(API_GATEWAY_CLIENT).get('/v2/product/ix/types', requestConfig)

    return ixTypes
  } catch (error) {
    console.error(`Failed to load IX types for location of ID ${locationId}`, error)
    throw error
  }
}

export async function loadFixedBandwidths(productUid, cloudServiceProvider) {
  const normalisedProductUid = internalToValue(productUid)
  const normalisedCloudServiceProvider = internalToValue(cloudServiceProvider)

  const isAlibabaOrAwshc = ['ALIBABA', 'AWSHC'].includes(normalisedCloudServiceProvider)

  try {
    const { data: bandwidths } = isAlibabaOrAwshc
      ? await getHttpClient(API_GATEWAY_CLIENT).get(`/v2/secure/${normalisedCloudServiceProvider.toLowerCase()}`)
      : await getHttpClient(API_GATEWAY_CLIENT).get(`/v2/secure/ibm/${normalisedProductUid}`)

    return bandwidths
  } catch (error) {
    console.error(
      `Failed to load bandwidths for ${normalisedCloudServiceProvider} product of UID ${normalisedProductUid}`,
      error,
    )
    throw error
  }
}

export async function loadMveImages() {
  try {
    const {
      data: { mveImages },
    } = await getHttpClient(API_GATEWAY_CLIENT).get('/v4/product/mve/images')
    return mveImages
  } catch (error) {
    console.error('Failed to load mve images', error)
    throw error
  }
}

export async function loadMveVariants() {
  try {
    const { data } = await getHttpClient(API_GATEWAY_CLIENT).get('/v3/product/mve/variants')
    return data
  } catch (error) {
    console.error('Failed to load mve variants', error)
    throw error
  }
}
